import { useEffect, useState, useCallback } from "react";
import { api, money, fmtDate } from "@/lib/api";
import { RecordForm } from "@/components/DataModule";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Repeat, Play, Pause, History as HistoryIcon } from "lucide-react";

const AB_FIELDS = [
  { key: "customer_id", label: "Customer", type: "lookup", entity: "customers", labelKey: "name", mirror: "customer_name" },
  { key: "site_id", label: "Site (optional)", type: "lookup", entity: "sites", labelKey: "name", mirror: "site_name" },
  { key: "description", label: "Service / job description", required: true, type: "textarea" },
  { key: "amount", label: "Billing amount (net)", type: "number" },
  { key: "vat_rate", label: "VAT %", type: "number" },
  { key: "billing_frequency", label: "Billing frequency", type: "select", options: ["Monthly", "Quarterly", "Biannual", "Annual"], default: "Monthly" },
  { key: "first_billing_date", label: "First billing date", type: "date" },
  { key: "start_date", label: "Start date", type: "date" },
  { key: "end_date", label: "End date (optional)", type: "date" },
];

export default function AutoBilling({ cur = "EUR", onGenerated }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [histItems, setHistItems] = useState(null);

  const load = useCallback(() => { api.get("/auto-billing").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ billing_frequency: "Monthly" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    if (!form.description) return toast.error("Description is required");
    if (!form.billing_frequency) return toast.error("Billing frequency is required");
    if (!form.first_billing_date) return toast.error("First billing date is required");
    setSaving(true);
    try {
      if (editing) await api.put(`/auto-billing/${editing.id}`, form);
      else await api.post("/auto-billing", form);
      toast.success("Auto billing job saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const toggle = async (r) => {
    const { data } = await api.post(`/auto-billing/${r.id}/toggle`);
    toast.success(data.active ? `${r.ref} activated — billing will resume` : `${r.ref} paused — no new billing items`);
    load();
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/auto-billing/run");
      toast.success(data.created ? `${data.created} billing item(s) generated onto the billing board` : "No billing items due yet");
      load(); onGenerated?.();
    } catch (e) { toast.error("Run failed"); } finally { setRunning(false); }
  };

  const openHistory = async (r) => {
    setHistFor(r); setHistItems(null);
    const { data } = await api.get(`/auto-billing/${r.id}/items`);
    setHistItems(data);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-xl">Subscription &amp; recurring services billed automatically. When the next billing date arrives an item is added to the billing board, badged <b>AUTO</b>, and the next date rolls forward. Duplicate periods are prevented.</p>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="ab-run-btn" onClick={run} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Repeat className="h-4 w-4 mr-1" />}Generate due items
          </Button>
          <Button className="btn-accent" data-testid="ab-new-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" />New Auto Billing Job</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Ref", "Description", "Customer", "Frequency", "Amount", "VAT %", "Next Billing", "Items", "Status", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={10} className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">No auto billing jobs yet. Create one for subscription or recurring billing.</td></tr>}
            {rows && rows.map((r) => (
              <tr key={r.id} data-testid={`ab-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5 font-medium accent-text whitespace-nowrap">{r.ref}</td>
                <td className="px-3 py-2.5 text-slate-800 max-w-[260px] truncate">{r.description}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.customer_name || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.billing_frequency}</td>
                <td className="px-3 py-2.5 text-slate-800 whitespace-nowrap">{money(r.amount, cur)}</td>
                <td className="px-3 py-2.5 text-slate-500">{r.vat_rate ?? "—"}</td>
                <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(r.next_billing_date)}</td>
                <td className="px-3 py-2.5">
                  <button data-testid={`ab-history-${r.id}`} onClick={() => openHistory(r)} className="inline-flex items-center gap-1 text-xs accent-text hover:underline">
                    <HistoryIcon className="h-3 w-3" />{r.items_generated}
                  </button>
                </td>
                <td className="px-3 py-2.5"><StatusBadge status={r.active ? "Active" : "Paused"} /></td>
                <td className="px-3 py-2.5 whitespace-nowrap text-right">
                  <Button variant="ghost" size="sm" data-testid={`ab-edit-${r.id}`} onClick={() => openEdit(r)}>Edit</Button>
                  <Button variant="ghost" size="sm" data-testid={`ab-toggle-${r.id}`} onClick={() => toggle(r)}>
                    {r.active ? <><Pause className="h-3.5 w-3.5 mr-1" />Pause</> : <><Play className="h-3.5 w-3.5 mr-1" />Activate</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? `Edit ${editing.ref}` : "New Auto Billing Job"}</DialogTitle>
            <DialogDescription>Recurring/subscription billing — items are generated automatically on each billing date.</DialogDescription></DialogHeader>
          <RecordForm fields={AB_FIELDS} value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-accent" data-testid="ab-save-btn" onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
        <DialogContent className="bg-white max-w-xl">
          <DialogHeader><DialogTitle className="font-head">Billing history · {histFor?.ref}</DialogTitle>
            <DialogDescription>{histFor?.description}</DialogDescription></DialogHeader>
          {histItems === null ? <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div> : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto scroll-thin" data-testid="ab-history-list">
              {histItems.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No billing items generated yet.</p>}
              {histItems.map((j) => (
                <div key={j.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2">
                  <span><span className="font-medium accent-text">{j.ref}</span> <span className="text-slate-500">· period {fmtDate(j.billing_period)}</span></span>
                  <span className="flex items-center gap-2"><span className="font-medium text-slate-700">{money(j.expected_value, cur)}</span><StatusBadge status={j.billing_status} /></span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
